import { NumberInput, Select, Switch, Tooltip } from "@mantine/core";
import { Info } from "lucide-react";
import type { ReactNode } from "react";

import type { Composer } from "../composer/use-composer";
import classes from "./advanced.module.css";

/** Live's info texts for the settings (`video-studio/constants.ts`). */
const TIPS = {
  seed: "Random seed for reproducibility. Note that results may still vary slightly even with the same seed.",
  fps: "The frames per second of the generated video",
  cfg: "The CFG (Classifier-Free Guidance) scale controls how closely the model follows your prompt. Lower values (closer to 0) allow more creative freedom, while higher values (closer to 1) make the result follow your prompt more strictly.",
  negative: "Content to avoid in the video.",
  enhance: "Whether to use the model's prompt optimiser",
  keepAudio: "Keep the original audio from the source video in the generated output",
} as const;

/** A number from an input, or null when it is empty. */
const numberOf = (value: string | number): number | null =>
  value === "" ? null : Number(value);

/**
 * Live's advanced settings side panel (#231, L05, L05b, `advanced-form-content`): a row per
 * setting the type's block takes, in live's order, each a label with its info tip and a 65px
 * control. "Seed control" is a whole number from 1; "FPS" the block's rates; "Guidance scale"
 * the block's range in steps of 0.1; "Negative prompt" a switch that shows its text under the
 * prompt; "Enhance prompt" a switch. A tool's "Keep Reference Audio" (#209, L05) sits after the
 * guidance scale, where the model can keep its source's sound.
 */
export function AdvancedPanel({ c }: { c: Composer }) {
  if (!c.advancedOffered) return null;
  const caps = c.advancedCaps ?? {};
  const values = c.draft.advanced;
  const fps = caps.fps ?? [];
  const cfg = caps.cfg_scale ?? null;
  const negative = caps.negative_prompt ?? null;
  return (
    <div className={classes.panel}>
      {caps.seed === true ? (
        <Field label="Seed control" tip={TIPS.seed}>
          {(tip) => (
            <NumberInput
              aria-label="Seed control"
              description={tip}
              classNames={{ description: classes.hidden }}
              size="xs"
              min={1}
              step={1}
              hideControls
              allowDecimal={false}
              allowNegative={false}
              clampBehavior="strict"
              value={values.seed ?? ""}
              onChange={(value) => c.setAdvanced({ seed: numberOf(value) })}
            />
          )}
        </Field>
      ) : null}
      {fps.length > 0 ? (
        <Field label="FPS" tip={TIPS.fps}>
          {(tip) => (
            <Select
              aria-label="FPS"
              description={tip}
              classNames={{ description: classes.hidden }}
              size="xs"
              data={fps.map((each) => String(each))}
              value={values.fps === null ? null : String(values.fps)}
              onChange={(value) => c.setAdvanced({ fps: value === null ? null : Number(value) })}
              comboboxProps={{ withinPortal: false }}
            />
          )}
        </Field>
      ) : null}
      {cfg === null ? null : (
        <Field label="Guidance scale" tip={TIPS.cfg}>
          {(tip) => (
            <NumberInput
              aria-label="Guidance scale"
              description={tip}
              classNames={{ description: classes.hidden }}
              size="xs"
              min={cfg.min}
              max={cfg.max}
              step={0.1}
              decimalScale={2}
              clampBehavior="strict"
              allowNegative={cfg.min < 0}
              value={values.cfgScale ?? ""}
              onChange={(value) => c.setAdvanced({ cfgScale: numberOf(value) })}
            />
          )}
        </Field>
      )}
      {c.keepsSourceAudio ? (
        <Field label="Keep Reference Audio" tip={TIPS.keepAudio}>
          {(tip) => (
            <Switch
              aria-label="Keep Reference Audio"
              description={tip}
              classNames={{ description: classes.hidden }}
              checked={c.draft.keepSourceAudio}
              onChange={(event) => c.set({ keepSourceAudio: event.currentTarget.checked })}
            />
          )}
        </Field>
      ) : null}
      {negative === null ? null : (
        <Field label="Negative prompt" tip={TIPS.negative}>
          {(tip) => (
            <Switch
              aria-label="Negative prompt"
              description={tip}
              classNames={{ description: classes.hidden }}
              checked={values.negative}
              onChange={(event) => c.setAdvanced({ negative: event.currentTarget.checked })}
            />
          )}
        </Field>
      )}
      {caps.prompt_enhancer === true ? (
        <Field label="Enhance prompt" tip={TIPS.enhance}>
          {(tip) => (
            <Switch
              aria-label="Enhance prompt"
              description={tip}
              classNames={{ description: classes.hidden }}
              checked={values.enhance}
              onChange={(event) => c.setAdvanced({ enhance: event.currentTarget.checked })}
            />
          )}
        </Field>
      ) : null}
    </div>
  );
}

interface FieldProps {
  label: string;
  tip: string;
  /** The control, given the tip's words as its (hidden) description, read out with it. */
  children: (tip: string) => ReactNode;
}

/**
 * Live's horizontal field: the label and its info icon on the left (the tip on hover), the
 * control on the right.
 */
function Field({ label, tip, children }: FieldProps) {
  return (
    <div className={classes.field}>
      <span className={classes.labelGroup}>
        <span className={classes.label}>{label}</span>
        <Tooltip label={tip} withArrow multiline maw={300}>
          <Info size={14} className={classes.info} aria-hidden />
        </Tooltip>
      </span>
      <span className={classes.control}>{children(tip)}</span>
    </div>
  );
}
